import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Home,
  Percent,
  Ticket,
  ArrowRight,
  Sparkles,
  X,
  Clock,
  BadgeCheck,
  Store,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  obtenerCuponesConSocios,
  usarCupon,
} from "../services/cuponService.tsx";
import { useAdminRole } from "../hooks/useAdminRole.ts";

type CuponAsignado = {
  id: number | string;
  codigo?: string;
  comercio?: string;
  descripcion?: string;
  descuento?: number | string;
  fechaExpiracion?: string;
  utilizado?: boolean;
  estado: "vigente" | "proximo" | "vencido";
};

const estadoStyles: Record<CuponAsignado["estado"], string> = {
  vigente: "bg-emerald-100 text-emerald-700 border-emerald-200",
  proximo: "bg-amber-100 text-amber-700 border-amber-200",
  vencido: "bg-rose-100 text-rose-700 border-rose-200",
};

const estadoLabels: Record<CuponAsignado["estado"], string> = {
  vigente: "Vigente",
  proximo: "Próximo a vencer",
  vencido: "Vencido",
};

const obtenerEstado = (fecha?: string) => {
  if (!fecha) return "proximo" as const;
  const hoy = new Date();
  const f = new Date(fecha);
  if (isNaN(f.getTime())) return "proximo" as const;
  if (f < hoy) return "vencido" as const;
  const diff = f.getTime() - hoy.getTime();
  const dias = diff / (1000 * 60 * 60 * 24);
  return dias <= 7 ? ("proximo" as const) : ("vigente" as const);
};

export default function CuponesSocio() {
  const [cupones, setCupones] = useState<CuponAsignado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cuponParaUsar, setCuponParaUsar] = useState<CuponAsignado | null>(null);
  const [usandoId, setUsandoId] = useState<string | number | null>(null);
  const [exitoMsg, setExitoMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();

  const socioSeleccionado = useMemo(() => {
    const stored = localStorage.getItem("socioSeleccionado");
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error("No se pudo parsear socioSeleccionado", e);
      return null;
    }
  }, []);

  useEffect(() => {
    const cargar = async () => {
      if (!socioSeleccionado?.socio_id) {
        setError("Seleccioná un socio para ver sus cupones.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const data = await obtenerCuponesConSocios(token);
        const asignados = Array.isArray(data)
          ? data.filter((item: any) => {
              const sociosAsignados =
                item?.sociosAsignados ??
                item?.socios ??
                item?.asignaciones ??
                item?.cupon?.sociosAsignados ??
                [];
              if (!Array.isArray(sociosAsignados)) return false;
              return sociosAsignados.some((s: any) => {
                const sid = s?.socioId ?? s?.socio_id ?? s?.id ?? s;
                return sid && String(sid) === String(socioSeleccionado.socio_id);
              });
            })
          : [];

        const normalizados: CuponAsignado[] = asignados.map((item: any) => {
          const base = item?.cupon || item;
          const fecha = base?.fechaExpiracion ?? base?.fecha_expiracion ?? base?.expira;
          const descuento = base?.descuento ?? base?.discount ?? base?.off;
          return {
            id: base?.id ?? base?.cuponId ?? base?.cupon_id ?? base?.codigo ?? Math.random().toString(36),
            codigo: base?.codigo,
            comercio: base?.comercio,
            descripcion: base?.descripcion,
            descuento,
            fechaExpiracion: fecha,
            utilizado: base?.utilizado ?? false,
            estado: obtenerEstado(fecha),
          };
        });

        setCupones(normalizados);
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los cupones.");
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [getAccessTokenSilently, socioSeleccionado]);

  const handleUsarCupon = async () => {
    if (!cuponParaUsar || !socioSeleccionado?.socio_id) return;
    setUsandoId(cuponParaUsar.id);
    setExitoMsg(null);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await usarCupon(socioSeleccionado.socio_id, Number(cuponParaUsar.id), token);
      setCupones((prev) =>
        prev.map((c) =>
          c.id === cuponParaUsar.id ? { ...c, utilizado: true } : c
        )
      );
      setExitoMsg(`Cupón de ${cuponParaUsar.comercio || "el comercio"} utilizado correctamente.`);
      setCuponParaUsar(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "No se pudo usar el cupón.";
      setError(msg);
      setCuponParaUsar(null);
    } finally {
      setUsandoId(null);
    }
  };

  const cuponesDisponibles = cupones.filter((c) => !c.utilizado && c.estado !== "vencido");
  const cuponesUsados = cupones.filter((c) => c.utilizado);
  const cuponesVencidos = cupones.filter((c) => c.estado === "vencido" && !c.utilizado);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <CalendarClock className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Cupones</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-100">
                <Ticket className="h-4 w-4" />
                Beneficios y cupones
              </p>
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">Tus cupones asignados</h1>
              <p className="text-sm text-indigo-50">
                Mostrá este cupón en el comercio para aplicar tu descuento.
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => navigate("/cupones")}
                className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20"
              >
                Gestionar cupones
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 shadow-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {exitoMsg && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 shadow-sm">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            {exitoMsg}
          </div>
        )}

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={`sk-${idx}`} className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-24 rounded bg-slate-200" />
                    <div className="h-4 w-36 rounded bg-slate-200" />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="h-14 rounded-lg bg-slate-200" />
                  <div className="h-14 rounded-lg bg-slate-200" />
                </div>
                <div className="mt-4 h-10 w-full rounded-lg bg-slate-200" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {cuponesDisponibles.length > 0 && (
              <section>
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-indigo-500" />
                  <h2 className="text-lg font-bold text-slate-900">Cupones disponibles</h2>
                  <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
                    {cuponesDisponibles.length}
                  </span>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {cuponesDisponibles.map((cupon) => (
                    <div
                      key={cupon.id}
                      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-200">
                              <Store className="h-6 w-6" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {cupon.comercio || "Comercio"}
                              </p>
                              <p className="text-sm font-semibold text-slate-900 truncate">
                                {cupon.descripcion || "Sin descripción"}
                              </p>
                            </div>
                          </div>
                          <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold border ${estadoStyles[cupon.estado]}`}>
                            {estadoLabels[cupon.estado]}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-2">
                          <div className="rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-3 py-3 text-center text-white shadow-inner">
                            <p className="text-[10px] uppercase tracking-widest text-white/70">Descuento</p>
                            <p className="text-2xl font-extrabold leading-none">
                              {cupon.descuento ? `${cupon.descuento}%` : "—"}
                            </p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-center">
                            <p className="text-[10px] uppercase tracking-widest text-slate-400">Expira</p>
                            <p className="text-sm font-bold text-slate-900">
                              {cupon.fechaExpiracion
                                ? new Date(cupon.fechaExpiracion).toLocaleDateString("es-AR", { day: "2-digit", month: "short" })
                                : "—"}
                            </p>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-slate-50 px-3 py-2 border border-slate-100">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs font-semibold text-slate-500">Código:</span>
                          <span className="text-xs font-bold text-slate-900">{cupon.codigo || "Sin código"}</span>
                        </div>

                        <button
                          onClick={() => setCuponParaUsar(cupon)}
                          disabled={usandoId === cupon.id}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:from-blue-700 hover:to-indigo-700 hover:shadow-blue-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <BadgeCheck className="h-4 w-4" />
                          Usar este cupón
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cuponesUsados.length > 0 && (
              <section>
                <div className="mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-lg font-bold text-slate-900">Cupones utilizados</h2>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                    {cuponesUsados.length}
                  </span>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {cuponesUsados.map((cupon) => (
                    <div
                      key={cupon.id}
                      className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 opacity-70"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {cupon.comercio || "Comercio"} — {cupon.codigo || "—"}
                        </p>
                        <p className="text-xs text-slate-500">Ya utilizado</p>
                      </div>
                      <span className="text-lg font-bold text-emerald-600">
                        {cupon.descuento ? `${cupon.descuento}%` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cuponesVencidos.length > 0 && (
              <section>
                <div className="mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-rose-400" />
                  <h2 className="text-lg font-bold text-slate-900">Cupones vencidos</h2>
                  <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                    {cuponesVencidos.length}
                  </span>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {cuponesVencidos.map((cupon) => (
                    <div
                      key={cupon.id}
                      className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50/50 px-4 py-3 opacity-60"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-500">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {cupon.comercio || "Comercio"} — {cupon.codigo || "—"}
                        </p>
                        <p className="text-xs text-slate-500">Vencido</p>
                      </div>
                      <span className="text-lg font-bold text-rose-500">
                        {cupon.descuento ? `${cupon.descuento}%` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cupones.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center shadow-sm">
                <Ticket className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-500">
                  No hay cupones asignados a este socio.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {cuponParaUsar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-200">
                  <BadgeCheck className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Usar cupón</h3>
                  <p className="text-xs text-slate-500">Confirmá para marcar como utilizado</p>
                </div>
              </div>
              <button onClick={() => setCuponParaUsar(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Store className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-900">{cuponParaUsar.comercio || "Comercio"}</span>
              </div>
              <p className="text-sm text-slate-600">{cuponParaUsar.descripcion || "Sin descripción"}</p>
              <div className="mt-3 flex items-center gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">Descuento</p>
                  <p className="text-xl font-extrabold text-indigo-600">{cuponParaUsar.descuento}%</p>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">Código</p>
                  <p className="text-sm font-bold text-slate-900">{cuponParaUsar.codigo || "—"}</p>
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-500">
              ¿Confirmás que usaste este cupón en el comercio? Esta acción no se puede deshacer.
            </p>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setCuponParaUsar(null)}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleUsarCupon}
                disabled={usandoId === cuponParaUsar.id}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {usandoId === cuponParaUsar.id ? (
                  "Procesando..."
                ) : (
                  <>
                    <BadgeCheck className="h-4 w-4" />
                    Sí, usar cupón
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
